import { useRouter } from 'next/router';
import { useCallback, useContext, useState } from 'react';
import { deleteCart, fetchCart, postCart } from 'src/libs/api/cartApi';
import type { CartItem } from 'src/models/orderLimits/schema';
import { AuthContext } from 'src/stores/Auth';
import type { LOCALE_TYPE } from 'src/types/locale';
import { syncCart } from 'src/utils/cart';
import { clearCartInCookie, getCartFromCookie, saveCartToCookie } from 'src/utils/cookies';
import { mixpanel } from 'src/utils/mixpanel';
import useSWR from 'swr';
import { useCartQuantity } from '../useCartQuantity';
import { useCurrentShop } from '../useCurrentShop';
import { useModal } from '../useModal';
import { getLocalCartKey, getUnLoggedInCart } from './helper';

export const useCart = (isProxy?: boolean) => {
  const { shopIdValue, currentShop } = useCurrentShop();
  const { locale } = useRouter();
  const currentShopId = currentShop?.id ?? shopIdValue;
  const { user } = useContext(AuthContext);
  const [isUpdating, setIsUpdating] = useState(false);
  const isLoggedIn = !!user;
  const { setIsVisible, setModalParams } = useModal('noticeModal');
  const { mutate: mutateQuantity } = useCartQuantity();
  const fetcher = useCallback(async () => {
    if (isProxy) return;

    if (isLoggedIn) {
      await syncCart(currentShopId);
      const response = await fetchCart();
      if (response.isLeft()) {
        throw new Error(response.value.errors.join(','));
      }
      return response.value;
    }
    const products = await getUnLoggedInCart(currentShopId, getCartFromCookie(), locale as LOCALE_TYPE);
    return {
      shopId: currentShopId,
      cart: { products }
    };
  }, [currentShopId, isLoggedIn, isProxy]);
  const { data, error, mutate } = useSWR(isLoggedIn ? '/api/cart' : getLocalCartKey('/api/cart'), fetcher, {
    dedupingInterval: 0
  });

  const updateCart = async (shopId: number, cart: CartItem[], section: string) => {
    if (isProxy) return;

    if (isLoggedIn) {
      if (isUpdating) return;
      setIsUpdating(true);
      const response = await postCart(shopId, cart);
      setIsUpdating(false);
      if (response.isLeft()) {
        setModalParams({
          type: 'warning',
          text: response.value.errors.join(',')
        });
        setIsVisible(true);
        return;
      }
      mixpanel.onCartChange(cart, section);
      // Update SWR cache
      await mutate();
      return response.value;
    }
    const newCart = cart.map((p) => ({ productId: p.productId, quantity: p.quantity }));
    saveCartToCookie(newCart);
    const products = await getUnLoggedInCart(currentShopId, newCart, locale as LOCALE_TYPE);
    mutate({ shopId: currentShopId, cart: { products } });
    mutateQuantity({ cartTotalQuantity: products.reduce((prev, current) => prev + current.quantity, 0) });
  };

  const removeCart = async () => {
    if (isProxy) return;

    if (isLoggedIn) {
      const response = await deleteCart();
      if (response.isLeft()) {
        setModalParams({
          type: 'warning',
          text: response.value.errors.join(',')
        });
        setIsVisible(true);
        return;
      }
      // Update SWR cache
      await mutate();
    } else {
      clearCartInCookie();
      mutate({ shopId: currentShopId, cart: { products: [] } });
      mutateQuantity({ cartTotalQuantity: 0 });
    }
  };

  return {
    savedShopId: data?.shopId,
    cart: data?.cart?.products ?? [],
    isLoading: !error && !data,
    isError: error,
    updateCart,
    removeCart
  };
};
